import {
  joinImagesUrl,
  publicOfferProductToUiProduct,
} from '../../../shared/utils/home-blocks.utils';
import { environment } from '../../../../environments/environment';
import type { BlogPost } from '../../../shared/models/blog.model';
import type { BlogPostCardApi } from '../../blog/data/blog.api-model';
import { toBlogPostCard } from '../../blog/data/blog-card.mapper';
import { toStoreEvents } from '../../events/data/events.mappers';
import type { BadgeVariant } from '../../../shared/ui/badge/badge.component';
import type {
  CommunityLink,
  GameIntroContent,
  GameIntroSection,
  GameSection,
  GameSectionSummary,
  GameStarterGuideContent,
  GameCommunityContent,
  LocalStore,
  StarterGuideGroup,
} from '../models/game-section.model';
import type {
  PublicCommunityLink,
  PublicGameCommunity,
  PublicGameIntro,
  PublicGameIntroSection,
  PublicGameSection,
  PublicGameSectionSummary,
  PublicLocalStore,
  PublicStarterGuide,
} from './game-section.model';

// Adaptador payload (snake_case) → contrato de la vista (camelCase).
//
// El mapper NO reimplementa reglas de omisión: el backend ya decidió qué se
// pinta y lo que no llega en `null`. Aquí solo se traduce, se resuelven las
// URLs de imagen y se normaliza lo que el tipo de la vista no admite
// (`null` en campos de texto → cadena vacía, variante de badge desconocida →
// sin variante). Duplicar las reglas de omisión aquí produciría dos
// implementaciones de lo mismo que acabarían divergiendo.

/** Variantes válidas de `<app-badge>`. Réplica de `BadgeVariant`, que es un tipo. */
const BADGE_VARIANTS: readonly BadgeVariant[] = [
  'new',
  'sale',
  'sold-out',
  'pre-sale',
  'info',
  'highlight',
  'vip',
  'success',
  'warning',
  'alert',
  'danger',
];

const COMMUNITY_PLATFORMS: readonly CommunityLink['platform'][] = [
  'whatsapp',
  'discord',
  'facebook',
  'instagram',
  'tiktok',
];

function text(value: string | null | undefined): string {
  return value ?? '';
}

/** `null`/vacío → `undefined`, que es lo que los campos opcionales de la vista esperan. */
function optionalText(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function imageUrl(path: string | null | undefined): string {
  return path ? joinImagesUrl(path) : '';
}

/**
 * El backend valida `badge_variant` contra la misma lista, así que un valor
 * fuera de ella solo puede venir de datos antiguos o de una divergencia de
 * contrato: se descarta y el carrusel cae a su variante por defecto en vez de
 * pasarle a `<app-badge>` una clase que no existe.
 */
function toBadgeVariant(value: string | null | undefined): BadgeVariant | undefined {
  if (!value) return undefined;
  return BADGE_VARIANTS.find((variant) => variant === value);
}

function toIntroSection(section: PublicGameIntroSection | null): GameIntroSection | null {
  if (!section) return null;
  return {
    heading: section.heading,
    paragraphs: section.paragraphs ?? [],
    imageUrl: imageUrl(section.image_url),
    imageAlt: text(section.image_alt),
    videoUrl: optionalText(section.video_url),
    videoCaption: optionalText(section.video_caption),
  };
}

function toIntro(intro: PublicGameIntro | null): GameIntroContent | null {
  if (!intro) return null;
  return {
    whatIs: toIntroSection(intro.what_is),
    howToPlay: toIntroSection(intro.how_to_play),
  };
}

function toStarterGuide(guide: PublicStarterGuide | null): GameStarterGuideContent | null {
  if (!guide) return null;

  const groups: StarterGuideGroup[] = (guide.groups ?? []).map((group) => ({
    title: group.title,
    items: (group.items ?? []).map((item) => ({
      product: publicOfferProductToUiProduct(item.product),
      badgeLabel: optionalText(item.badge_label),
      badgeVariant: toBadgeVariant(item.badge_variant),
    })),
  }));

  return {
    intro: guide.intro
      ? { heading: guide.intro.heading, paragraphs: guide.intro.paragraphs ?? [] }
      : undefined,
    groups,
  };
}

/**
 * Descarta plataformas fuera de las que la vista sabe iconizar. El
 * backend valida contra la misma lista; el filtro es la garantía de que el
 * tipo `CommunityLink['platform']` sigue diciendo la verdad.
 */
function toCommunityLink(link: PublicCommunityLink): CommunityLink | null {
  const platform = COMMUNITY_PLATFORMS.find((p) => p === link.platform);
  if (!platform) return null;
  return {
    platform,
    label: text(link.label) || link.url,
    url: link.url,
    memberCount: optionalText(link.member_count),
  };
}

function toLocalStore(store: PublicLocalStore): LocalStore {
  return {
    name: store.name,
    address: text(store.address),
    scheduleNote: optionalText(store.schedule_note),
  };
}

function toCommunity(community: PublicGameCommunity | null): GameCommunityContent | null {
  if (!community) return null;
  return {
    // Mismo contrato congelado que el listado y el calendario: se reutiliza el
    // mapper del módulo de Eventos, que es su dueño.
    upcomingEvents: toStoreEvents(community.upcoming_events),
    onlineLinks: (community.online_links ?? [])
      .map(toCommunityLink)
      .filter((link): link is CommunityLink => link !== null),
    localStores: (community.local_stores ?? []).map(toLocalStore),
  };
}

/** Payload hidratado de `GET /api/web/games/{slug}` → sección lista para pintar. */
export function publicGameSectionToUi(payload: PublicGameSection): GameSection {
  return {
    slug: payload.slug,
    name: payload.name,
    // El nombre corto solo rotula CTAs ("Ver catálogo de …"): si el admin no lo
    // escribió, el nombre completo es un sustituto correcto, no un hueco.
    shortName: text(payload.short_name) || payload.name,
    tagline: text(payload.tagline),
    accentColor: text(payload.accent_color),
    accentColorLight: text(payload.accent_color_light),
    logoUrl: imageUrl(payload.logo_url),
    heroLogoUrl: imageUrl(payload.hero_logo_url),
    heroImageUrl: imageUrl(payload.hero_image_url),
    categorySlug: payload.category_slug,
    metaTitle: payload.meta_title,
    metaDescription: payload.meta_description,
    intro: toIntro(payload.intro),
    starterGuide: toStarterGuide(payload.starter_guide),
    community: toCommunity(payload.community),
    blogPosts: gameLandingBlogPosts(payload.blog_posts, environment.blogEnabled),
  };
}

/**
 * Bloque `blog_posts` de la landing (B86, B126): tarjetas del blog con el
 * mismo mapper que el listado. Con el blog apagado se descartan: enlazarían a
 * `/blog/…`, que sin sus rutas cae en el catch-all. La omisión (`[]` sin
 * entradas) la decide la API. Pura y con el flag como parámetro porque Karma
 * compila con `environment.ts` y solo vería un valor (B58).
 */
export function gameLandingBlogPosts(
  cards: readonly BlogPostCardApi[] | null | undefined,
  blogEnabled: boolean,
): BlogPost[] {
  return blogEnabled ? (cards ?? []).map(toBlogPostCard) : [];
}

/** Fila del índice `GET /api/web/games` → resumen para el header/megamenú. */
export function publicGameSummaryToUi(row: PublicGameSectionSummary): GameSectionSummary {
  return {
    slug: row.slug,
    name: row.name,
    shortName: text(row.short_name) || row.name,
    tagline: text(row.tagline),
    accentColor: text(row.accent_color),
    accentColorLight: text(row.accent_color_light),
    logoUrl: imageUrl(row.logo_url),
    categorySlug: row.category_slug,
  };
}
